export const state = () => ({
  startTime: null,
  profileId: null,
  siteId: null,
  siteProfileId: null, // Add site profile ID for easier matching with route params
  // Define the threshold in state for consistency and potential configuration later
  thresholdMilliseconds: 10000, // 5 minutes // This is for the navigation away message
  isTabActive: process.client ? document.visibilityState === 'visible' : true,
  // Track accumulated time for when tab becomes inactive
  accumulatedTimeMs: 0,
  lastPauseTime: null,
  // Current timestamp for reactive time calculations
  currentTimestamp: Date.now(),
  pauseCounter: 0,
  communicationLogId: null, // ID of the communication log entry for the current session
  autoLogIntervalMilliseconds: 30000, // Changed to 30 seconds for auto-logging
  currentNote: '', // Holds the current note for the session
  currentNoteId: null, // Holds the ID of the note associated with the communicationLogId
  currentPurpose: 'PROFILE_USER_ACTIVITY', // Default purpose for activity tracking
  // Timer alert modal state
  showTimerAlert: false,
  timerAlertShownAt: null, // Track when alert was first shown
  lastAlertTriggeredAt: 0, // Track the active time when alert was last triggered
  maxActiveTimeMs: 40 * 60 * 1000, // 40 minutes
  autoPauseTimeMs: 42 * 60 * 1000, // 42 minutes
});

export const mutations = {
  START_TRACKING (state, { profileId, siteId, siteProfileId }) {
    // Store as timestamp instead of Date object for better Vuex reactivity
    state.startTime = Date.now();
    state.profileId = profileId;
    state.siteId = siteId;
    state.siteProfileId = siteProfileId; // Store site profile ID
    state.accumulatedTimeMs = 0;
    state.communicationLogId = null; // Reset log ID when new tracking starts
    state.currentNote = ''; // Reset note on new tracking session
    state.currentNoteId = null; // Reset note ID on new tracking session
    state.currentPurpose = 'PROFILE_USER_ACTIVITY'; // Reset to default purpose
    
    // Handle initial tab visibility correctly
    const isVisible = process.client ? document.visibilityState === 'visible' : true;
    if (!isVisible) {
      // If starting while hidden, initialize as paused
      state.isTabActive = false;
      state.lastPauseTime = Date.now(); // Set pause time to start time
      state.pauseCounter = 1; // Start with pause count of 1
      console.log(`[ProfileActivity] Starting tracking in hidden tab - initialized as paused`);
    } else {
      // Starting while visible
      state.isTabActive = true;
      state.lastPauseTime = null;
      state.pauseCounter = 0;
    }
    
    state.currentTimestamp = Date.now();
    console.log(`[ProfileActivity] Mutation executed, startTime set to: ${state.startTime}, profileId: ${profileId}, siteProfileId: ${siteProfileId}, isTabActive: ${state.isTabActive}, pauseCounter: ${state.pauseCounter}`);
  },
  RESET_TRACKING (state) {
    state.startTime = null;
    state.profileId = null;
    state.siteId = null;
    state.siteProfileId = null;
    state.accumulatedTimeMs = 0;
    state.lastPauseTime = null;
    state.pauseCounter = 0;
    state.isTabActive = true;
    state.communicationLogId = null; // Clear log ID
    state.currentNote = ''; // Reset note
    state.currentNoteId = null; // Reset note ID
    state.currentPurpose = 'PROFILE_USER_ACTIVITY'; // Reset to default purpose
    state.showTimerAlert = false;
    state.timerAlertShownAt = null;
    state.lastAlertTriggeredAt = 0;
    console.log('[ProfileActivity] Tracking reset');
  },
  PAUSE_TRACKING (state) {
    if (state.startTime) {
      if (state.pauseCounter === 0) { // Pausing from an active state
        state.lastPauseTime = Date.now();
      }
      state.pauseCounter++;
      state.isTabActive = false; // Keep this consistent with paused state
      console.log(`[ProfileActivity] Tracking paused. Pause count: ${state.pauseCounter}`);
    }
  },
  RESUME_TRACKING (state) {
    if (state.startTime && state.pauseCounter > 0) {
      state.pauseCounter--;
      if (state.pauseCounter === 0) { // Resuming to a fully active state
        if (state.lastPauseTime) {
          const pauseDuration = Date.now() - state.lastPauseTime;
          state.accumulatedTimeMs = (state.accumulatedTimeMs || 0) + pauseDuration;
          state.lastPauseTime = null;
        }
        state.isTabActive = true;
        state.currentTimestamp = Date.now();
        console.log(`[ProfileActivity] Tracking resumed.`);
      } else {
        console.log(`[ProfileActivity] Tracking remains paused. Pause count: ${state.pauseCounter}`);
      }
    }
  },
  UPDATE_TIMESTAMP (state) {
    state.currentTimestamp = Date.now();
  },
  SET_COMMUNICATION_LOG_ID (state, logId) {
    state.communicationLogId = logId;
    console.log(`[ProfileActivity] Communication log ID set to: ${logId}`);
  },
  CLEAR_COMMUNICATION_LOG_ID (state) {
    state.communicationLogId = null;
    console.log('[ProfileActivity] Communication log ID cleared');
  },
  SET_CURRENT_NOTE(state, note) {
    state.currentNote = note || '';
    console.log(`[ProfileActivity] Current note set to: "${state.currentNote}"`);
  },
  SET_CURRENT_NOTE_ID(state, noteId) {
    state.currentNoteId = noteId;
    console.log(`[ProfileActivity] Current note ID set to: "${state.currentNoteId}"`);
  },
  SET_CURRENT_PURPOSE(state, purpose) {
    state.currentPurpose = purpose || 'PROFILE_USER_ACTIVITY';
    console.log(`[ProfileActivity] Current purpose set to: "${state.currentPurpose}"`);
  },
  ADJUST_START_TIME(state, adjustmentMs) {
    if (state.startTime) {
      // To add time to the timer, we subtract from the start time.
      // To subtract time, we add to the start time.
      const newStartTime = state.startTime - adjustmentMs;
      
      // Ensure the new start time doesn't result in negative active time
      // Calculate what the active time would be with the new start time
      const currentTime = Date.now();
      const accumulatedMs = state.accumulatedTimeMs || 0;
      const potentialActiveTime = currentTime - newStartTime - accumulatedMs;
      
      if (potentialActiveTime < 0) {
        // If it would be negative, set start time so active time is exactly 0
        state.startTime = currentTime - accumulatedMs;
        console.log(`[ProfileActivity] Adjustment would result in negative time, capped at 0. New startTime: ${state.startTime}`);
      } else {
        state.startTime = newStartTime;
        console.log(`[ProfileActivity] startTime adjusted by ${-adjustmentMs}ms. New startTime: ${state.startTime}`);
      }
      
      // Also update currentTimestamp to force re-computation in getters immediately
      state.currentTimestamp = Date.now();
    }
  },
  SHOW_TIMER_ALERT(state, activeTimeMs) {
    state.showTimerAlert = true;
    state.timerAlertShownAt = Date.now();
    state.lastAlertTriggeredAt = activeTimeMs;
    console.log('[ProfileActivity] Timer alert modal opened');
  },
  HIDE_TIMER_ALERT(state) {
    state.showTimerAlert = false;
    state.timerAlertShownAt = null;
  },
  CONTINUE_TIMING_FROM_ALERT(state) {
    // Just hide the modal and continue timing from current position
    // The timer continues running, and the modal will show again after another interval
    state.showTimerAlert = false;
    state.timerAlertShownAt = null;
    state.currentTimestamp = Date.now();
  }
};

export const actions = {
  startTracking ({ commit }, { profileId, siteId, siteProfileId }) {
    console.log(`[ProfileActivity] startTracking action called with profileId=${profileId}, siteId=${siteId}, siteProfileId=${siteProfileId}`);
    commit('START_TRACKING', { profileId, siteId, siteProfileId });
    // console.log('[ProfileActivity] startTime set to:', new Date()); // startTime is a timestamp
  },
  resetTracking ({ commit }) {
    commit('RESET_TRACKING');
  },
  pauseTracking({ commit }) {
    commit('PAUSE_TRACKING');
  },
  resumeTracking({ commit }) {
    commit('RESUME_TRACKING');
  },
  updateTimestamp({ commit }) {
    commit('UPDATE_TIMESTAMP');
  },
  setCurrentNote({ commit }, note) {
    commit('SET_CURRENT_NOTE', note);
  },
  setCurrentPurpose({ commit }, purpose) {
    commit('SET_CURRENT_PURPOSE', purpose);
  },
  adjustTime({ commit, getters }, minutes) {
    if (getters.isTracking) {
      const adjustmentMs = minutes * 60 * 1000;
      commit('ADJUST_START_TIME', adjustmentMs);
    }
  },
  async logActivity ({ state, commit, dispatch, rootGetters, getters }, { noteText, isFinalLog = false, isAutoLog = false, skipNoteUpdate = false }) {
    // Only update the note if skipNoteUpdate is false AND noteText is provided
    if (!skipNoteUpdate && noteText !== undefined) {
        commit('SET_CURRENT_NOTE', noteText);
    }
    const noteForThisLog = state.currentNote;

    if (!state.profileId || !state.startTime) {
      console.log('[ProfileActivity] logActivity: No profileId or startTime, skipping log.');
      if (isFinalLog) commit('RESET_TRACKING');
      return null;
    }

    const activeMs = getters.activeTimeSpentMs;
    const durationInSeconds = Math.round(activeMs / 1000);

    const isTryingToCreateFirstLog = !state.communicationLogId;
    // For skipping initial empty log, only consider duration. Note can be added later.
    const isContentEmptyForInitialLog = durationInSeconds <= 0 && !noteForThisLog;
    const isPeriodicTimerCall = isAutoLog && (noteText === undefined);
    
    if (!isFinalLog && isTryingToCreateFirstLog && isContentEmptyForInitialLog && !isPeriodicTimerCall) {
        console.log('[ProfileActivity] logActivity: Skipped initial empty log (not a periodic timer call attempting to create first log, or has content).');
        return state.communicationLogId; 
    }
    
    const subject = `Patient Profile Activity`;

    console.log(`[ProfileActivity] logActivity: Proceeding. Duration: ${durationInSeconds}s, Note: "${noteForThisLog}", Log ID: ${state.communicationLogId || 'NEW'}, Note ID: ${state.currentNoteId || 'NONE'}, isPeriodic: ${isPeriodicTimerCall}, skipNoteUpdate: ${skipNoteUpdate}`);

    try {
      let currentLogId = state.communicationLogId;

      if (currentLogId) {
        // UPDATE existing log
        console.log(`[ProfileActivity] Updating communication log ${currentLogId} with ${durationInSeconds} seconds.`);
        
        // Update the encounter/duration part of the log
        const logUpdatePayload = {
          id: currentLogId,
          medium: 'NONE', // These might need to be fetched from existing log if they can change
          direction: 'NONE',
          purpose: state.currentPurpose, // Use the selected purpose from state
          subject,
          communicationResult: 'PATIENT_UPDATED',
          practitioner: rootGetters['portal/userId'],
          // notes and noteText are handled separately for updates
          encounter: {
            durationInSeconds,
          }
        };
        await dispatch('communicationLogs/updateLog', { logPayload: logUpdatePayload }, { root: true });

        // Now handle the note update/creation - but only if we're not skipping note updates
        if (!skipNoteUpdate) {
          if (noteForThisLog) { // Only try to update/add note if there is content
            if (state.currentNoteId) {
              console.log(`[ProfileActivity] Updating note ${state.currentNoteId} for log ${currentLogId}.`);
              await dispatch('communicationLogs/updateNoteOnLog', {
                noteId: state.currentNoteId,
                notePayload: { id: state.currentNoteId, text: noteForThisLog } // Assuming API expects {text: "..."}
              }, { root: true });
            } else {
              console.log(`[ProfileActivity] Adding new note to existing log ${currentLogId}.`);
              const newNote = await dispatch('communicationLogs/addNoteToLog', {
                logId: currentLogId,
                notePayload: { text: noteForThisLog } // Assuming API expects {text: "..."}
              }, { root: true });
              if (newNote && newNote.id) {
                commit('SET_CURRENT_NOTE_ID', newNote.id);
              } else {
                console.error('[ProfileActivity] addNoteToLog did not return a valid new note with ID.');
              }
            }
          } else if (state.currentNoteId) {
             // If note content is cleared, and a noteId exists, we might want to delete or clear the note on the backend.
             // For now, let's assume clearing the note means updating it to empty text.
             // Or, if the backend supports, dispatch a deleteNoteOnLog.
             // This part needs clarification based on desired behavior for clearing a note.
             // Let's update to empty if a note exists and current text is empty.
              console.log(`[ProfileActivity] Clearing content of note ${state.currentNoteId} for log ${currentLogId}.`);
              await dispatch('communicationLogs/updateNoteOnLog', {
                noteId: state.currentNoteId,
                notePayload: { id: state.currentNoteId, text: '' }
              }, { root: true });
          }
        } else {
          console.log(`[ProfileActivity] Skipping note update for log ${currentLogId} due to skipNoteUpdate=true.`);
        }

      } else {
        // CREATE new log
        console.log(`[ProfileActivity] Creating new communication log with ${durationInSeconds} seconds.`);
        const createLogPayload = {
          medium: 'NONE',
          direction: 'NONE',
          purpose: state.currentPurpose, // Use the selected purpose from state
          subject,
          communicationResult: 'PATIENT_UPDATED',
          practitioner: rootGetters['portal/userId'],
          assignedPractitioner: null,
          payload: null,
          notes: noteForThisLog ? [{ text: noteForThisLog }] : [], // Send note with create
          sent: new Date().toISOString(),
          encounter: {
            durationInSeconds,
          }
        };
        const newLog = await dispatch('communicationLogs/createLog', {
          profileId: state.profileId,
          logData: createLogPayload
        }, { root: true });

        if (newLog && newLog.id) {
          commit('SET_COMMUNICATION_LOG_ID', newLog.id);
          currentLogId = newLog.id; // Update currentLogId for this scope
          // Check if the newLog object contains the note and its ID
          if (noteForThisLog && newLog.notes && newLog.notes.length > 0 && newLog.notes[0].id) {
            commit('SET_CURRENT_NOTE_ID', newLog.notes[0].id);
          } else if (noteForThisLog) {
            console.warn('[ProfileActivity] New log created but note ID was not found in the response notes array. Note might not have been saved or ID not returned.');
            // Potentially try to add it separately if critical, or rely on next update cycle
          }
        } else {
            console.error('[ProfileActivity] createLog did not return a valid ID.');
        }
      }

      if (isFinalLog) {
        commit('RESET_TRACKING');
      }
      return currentLogId;
    } catch (error) {
      console.error('Failed to log profile activity:', error);
      // Don't reset tracking on failure so user can try again if not a final log
      // However, if it was a final log attempt and failed, still reset to avoid broken states.
      if (isFinalLog) {
        commit('RESET_TRACKING');
      }
      return null; // Indicate failure
    }
  },
  autoLogPeriodic({ dispatch, getters, state }) {
    if (!getters.isTracking || !state.isTabActive) {
      console.log('[ProfileActivity] autoLogPeriodic: Not tracking or tab inactive, skipping.');
      return;
    }
    
    const activeMs = getters.activeTimeSpentMs;
    if (activeMs > 0) { // Only log if there's some active time
        console.log(`[ProfileActivity] autoLogPeriodic: Dispatching logActivity. Total active ms: ${activeMs}`);
        dispatch('logActivity', { isFinalLog: false, isAutoLog: true });
    } else {
        console.log('[ProfileActivity] autoLogPeriodic: No active ms to log yet.');
    }
  },
  showTimerAlert({ commit, getters }) {
    const activeTimeMs = getters.activeTimeSpentMs;
    commit('SHOW_TIMER_ALERT', activeTimeMs);
  },
  hideTimerAlert({ commit }) {
    commit('HIDE_TIMER_ALERT');
  },
  continueTimingFromAlert({ commit, state }) {
    // If timer was paused, resume it first
    if (!state.isTabActive && state.pauseCounter > 0) {
      console.log('[ProfileActivity] Timer resumed from modal');
      commit('RESUME_TRACKING');
    }
    commit('CONTINUE_TIMING_FROM_ALERT');
  },
  exitProfileFromAlert({ commit, dispatch }) {
    // Log final activity before exiting
    dispatch('logActivity', { isFinalLog: true, isAutoLog: true, skipNoteUpdate: true });
    commit('HIDE_TIMER_ALERT');
  },
  autoPauseTimer({ commit, dispatch, state }) {
    if (state.showTimerAlert) {
      // Just pause the timer, keep modal open, stay in profile
      commit('PAUSE_TRACKING');
      console.log('[ProfileActivity] Timer auto-paused');
    }
  }
};

export const getters = {
  isTracking: state => {
    return !!state.startTime;
  },
  // Calculate active time spent on the profile
  activeTimeSpentMs: state => {
    if (!state.startTime) return 0;
    
    let activeTime = 0;
    const accumulatedMs = state.accumulatedTimeMs || 0; // Ensure it's a number
    
    // Base time is current time minus start time
    if (state.isTabActive) {
      // Currently active: calculate time from start minus any accumulated pause time
      activeTime = state.currentTimestamp - state.startTime - accumulatedMs;
    } else if (state.lastPauseTime) {
      // Currently paused: only count time up to when it was paused
      activeTime = state.lastPauseTime - state.startTime - accumulatedMs;
    } else {
      // This should not happen with proper initialization, but safe fallback
      activeTime = 0;
    }
    
    return Math.max(0, activeTime);
  },
  currentActivityMinutes: (state, getters) => {
    const ms = getters.activeTimeSpentMs;
    return Math.floor(ms / 60000);
  },
  // Getter primarily used for checking if threshold is met *at the moment it's called*
  shouldShowActivityToast: (state, getters) => { // Renamed from shouldShowTrackingModal
    if (!state.startTime) {
      return false;
    }
    const activeTime = getters.activeTimeSpentMs;
    return activeTime >= state.autoLogIntervalMilliseconds;
  },
  shouldShowTimerAlert: (state, getters) => {
    if (!state.startTime || state.showTimerAlert) {
      return false;
    }
    const activeTime = getters.activeTimeSpentMs;
    
    // Show modal when we've passed another full interval since the last alert
    const timeSinceLastAlert = activeTime - state.lastAlertTriggeredAt;
    return timeSinceLastAlert >= state.maxActiveTimeMs;
  },
  shouldAutoPauseTimer: (state, getters) => {
    if (!state.timerAlertShownAt) {
      return false;
    }
    const timeSinceAlert = Date.now() - state.timerAlertShownAt;
    return timeSinceAlert >= (2 * 60 * 1000); // 2 minutes
  }
}; 